import React, { useEffect, useState } from 'react';
import { Upload, Input, Space, Typography, message, Table, Button, Row } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import type { UploadProps } from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import { uploadAdditionalDocument } from '@app/api/follow-up.api';

const { Dragger } = Upload;
const { Text } = Typography;

interface DoumentCheckListProps {
  follow_up_id: string;
  editable?: boolean;
  canUpload?: any;
  additionalSubmittedDocs: {
    id: number;
    document_id: string;
    name: string;
    file: string;
    image_url: string;
  }[];
  onChange?: (file: UploadFile, documentName: string, documentId: string) => void;
}

const generateDocumentId = (name: string): string => {
  return name.trim().toLowerCase().replace(/\s+/g, '_') + '_' + Date.now();
};

const DoumentCheckList: React.FC<DoumentCheckListProps> = ({
  follow_up_id,
  additionalSubmittedDocs,
  editable,
  onChange,
  canUpload,
}) => {
  const [documentName, setDocumentName] = useState('');
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [editingDocumentId, setEditingDocumentId] = useState<string | null>(null);
  const [tableData, setTableData] = useState<any[]>([]);

  //console.log('Passed doc data: ', additionalSubmittedDocs);

  useEffect(() => {
    console.log('Passed doc data: ', additionalSubmittedDocs);
    if (additionalSubmittedDocs?.length > 0) {
      setTableData(
        additionalSubmittedDocs.map((doc) => ({
          ...doc,
          image_url: doc.image_url || doc.file,
        })),
      );
    } else {
      setTableData([]);
    }
  }, [additionalSubmittedDocs]);

  const updateTableData = (uploadedDocs: any[]) => {
    setTableData((prev) => {
      const updated = [...prev];

      uploadedDocs.forEach((doc: any) => {
        const existingIndex = updated.findIndex((item) => item.document_id === doc.document_id);

        const mappedDoc = {
          ...doc,
          image_url: doc.document_url,
        };

        if (existingIndex !== -1) {
          updated[existingIndex] = mappedDoc;
        } else {
          updated.push(mappedDoc);
        }
      });

      return updated;
    });
  };

  const props: UploadProps = {
    multiple: false,

    beforeUpload: async (file) => {
      if (!documentName) {
        message.error('Please enter a document name first.');
        return Upload.LIST_IGNORE;
      }

      const documentId = editingDocumentId ?? generateDocumentId(documentName);

      setUploading(true);

      try {
        const formData = new FormData();

        formData.append('name', documentName);
        formData.append('document_id', documentId);
        formData.append('file', file);

        const res = await uploadAdditionalDocument(Number(follow_up_id), formData);

        const uploadedDocs = (res as any)?.data?.followup_documents ?? [];

        updateTableData(uploadedDocs);

        setFileList([file]);

        if (onChange) {
          onChange(file as UploadFile, documentName, documentId);
        }

        message.success(`${file.name} uploaded successfully`);

        setDocumentName('');
        setFileList([]);
        setEditingDocumentId(null);
      } catch (err) {
        message.error('Failed to upload file.');
      } finally {
        setUploading(false);
      }

      return false;
    },

    onRemove: () => {
      setFileList([]);
    },

    fileList,
    showUploadList: true,
  };

  const handleView = (documentId: string, fileUrl: string) => {
    window.open(fileUrl, '_blank');
  };

  const handleEdit = (record: { document_id: string; name: string }) => {
    setDocumentName(record.name);
    setEditingDocumentId(record.document_id);
    setFileList([]);

    message.info(`Editing "${record.name}"`);
  };

  const handleNameOnlyUpdate = async () => {
    if (!documentName || !editingDocumentId) {
      message.error('Missing document name or ID.');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();

      formData.append('name', documentName);
      formData.append('document_id', editingDocumentId);

      const res = await uploadAdditionalDocument(Number(follow_up_id), formData);

      const uploadedDocs = (res as any)?.data?.followup_documents ?? [];

      updateTableData(uploadedDocs);

      message.success('Document name updated successfully');

      if (onChange) {
        onChange({} as UploadFile, documentName, editingDocumentId);
      }

      setDocumentName('');
      setEditingDocumentId(null);
    } catch (err) {
      message.error('Failed to update document name.');
    } finally {
      setUploading(false);
    }
  };

  const columns = [
    {
      title: 'Document Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Action',
      key: 'action',
      render: (_: any, record: { document_id: string; image_url: string; name: string }) => (
        <Space>
          <Button onClick={() => handleView(record.document_id, record.image_url)} type="primary" size="small">
            View
          </Button>

          {canUpload && (
            <Button onClick={() => handleEdit(record)} size="small">
              Edit
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ width: '91%' }}>
      <Row gutter={[16, 16]} style={{ width: '91%' }}>
        <Space direction="vertical" style={{ width: '91%' }}>
          {canUpload && (
            <>
              <Input
                placeholder="Enter document name"
                value={documentName}
                onChange={(e) => setDocumentName(e.target.value)}
                disabled={uploading}
                style={{ width: '100%' }}
              />

              {editingDocumentId && (
                <Text type="warning">
                  Editing document ID: <b>{editingDocumentId}</b>
                </Text>
              )}

              <Dragger {...props} disabled={uploading} style={{ width: '100%' }}>
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>

                <p className="ant-upload-text">Click or drag file to this area to upload</p>

                <p className="ant-upload-hint">Only one file can be uploaded per document.</p>

                {documentName && (
                  <Text type="secondary">
                    Document Name: <b>{documentName}</b>
                  </Text>
                )}
              </Dragger>

              {editingDocumentId && (
                <Space style={{ marginTop: '8px' }}>
                  <Button onClick={handleNameOnlyUpdate} loading={uploading} type="dashed">
                    Save Name Only
                  </Button>

                  <Button
                    danger
                    type="link"
                    onClick={() => {
                      setDocumentName('');
                      setEditingDocumentId(null);
                      setFileList([]);
                    }}
                  >
                    Cancel
                  </Button>
                </Space>
              )}
            </>
          )}

          <Table
            columns={columns}
            dataSource={tableData}
            rowKey="document_id"
            pagination={false}
            style={{
              marginTop: canUpload ? '16px' : 0,
              width: '91%',
            }}
          />
        </Space>
      </Row>
    </div>
  );
};

export default DoumentCheckList;
